import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRerunMutation, useTenantConfigQuery } from '../../services/routing/queries';
import type { RerunBody } from '../../types/routing';

const ENGINES = ['osrm', 'valhalla', 'myle', 'manual'];

/**
 * Try a configuration against a run that already happened. The run's own selection — its day,
 * fleets, companies and the routes it queued — is not restated here: the server reads it off the
 * run, which is the point of a rerun.
 *
 * <p>A value set here belongs to the one run it starts. The runtime overrides on the Admin tab
 * are the other tool, and the difference matters: those apply to every run started afterwards,
 * including that night's nightly, and stay until someone clears them.
 */
export const RerunPanel = ({
  runId,
  tenant,
  onClose
}: {
  runId: string;
  /** The tenant the source run planned — whose values the boxes read, not the header's. */
  tenant: string;
  onClose: () => void;
}) => {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [engine, setEngine] = useState('');
  const [notifyPlatform, setNotifyPlatform] = useState(false);
  const [liveWorld, setLiveWorld] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const rerun = useRerunMutation(runId);
  const { data: config, isLoading, error } = useTenantConfigQuery(tenant);

  const changed = Object.entries(edits).filter(
    ([name, value]) => value.trim() !== '' && value.trim() !== config?.effective[name]
  );

  const onStart = () => {
    const body: RerunBody = {};
    if (changed.length) {
      body.parameters = changed.map(([name, value]) => ({ name, value: value.trim() }));
    }
    if (engine) body.engine = engine;
    if (notifyPlatform) body.notify_platform = true;
    // A run that tells the platform is live on the server whatever is sent, so only a silent
    // rerun states it.
    if (liveWorld && !notifyPlatform) body.world = 'live';
    rerun.mutate(body, {
      onSuccess: result => {
        enqueueSnackbar(`Run #${result.run_id} re-plans run #${result.rerun_of}`);
        // Closed before the screen moves: the panel would otherwise stay open on the new run,
        // offering to re-run a run nobody has looked at yet.
        onClose();
        navigate(`/runs/${result.run_id}`);
      },
      onError: mutationError =>
        enqueueSnackbar((mutationError as Error).message, { variant: 'error' })
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        RE-RUN THIS RUN
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Plans the same day, tenant, fleets, their order, companies, the very routes this run
        queued and the very rides it saw — with the values below on top, for this one run only.
        Nothing here is saved against the tenant or the service, so it cannot reach
        tonight&apos;s nightly run. Leave everything untouched to re-plan the day under the
        configuration in force now.
      </Typography>

      {isLoading && <CircularProgress aria-label="loading parameters" />}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {(error as Error).message} — you can still re-run the day under the configuration in
          force, but the values below cannot be read to change one.
        </Alert>
      )}

      {config && (
        <TableContainer sx={{ maxHeight: 380, mb: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50%' }}>Parameter</TableCell>
                <TableCell sx={{ width: '16%' }}>In force</TableCell>
                <TableCell>Try</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.catalog.map(parameter => (
                <TableRow key={parameter.name}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {parameter.name} <em>({parameter.unit})</em>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {parameter.description}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {config.effective[parameter.name]}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {parameter.options ? (
                      <TextField
                        fullWidth
                        select
                        size="small"
                        SelectProps={{
                          displayEmpty: true,
                          SelectDisplayProps: { 'aria-label': parameter.name }
                        }}
                        value={edits[parameter.name] ?? ''}
                        onChange={event =>
                          setEdits(previous => ({
                            ...previous,
                            [parameter.name]: event.target.value
                          }))
                        }
                      >
                        <MenuItem value="">(unchanged)</MenuItem>
                        {parameter.options.map(option => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        inputProps={{ 'aria-label': parameter.name }}
                        placeholder={config.effective[parameter.name]}
                        value={edits[parameter.name] ?? ''}
                        onChange={event =>
                          setEdits(previous => ({
                            ...previous,
                            [parameter.name]: event.target.value
                          }))
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
        <TextField
          select
          label="Engine"
          size="small"
          value={engine}
          onChange={event => setEngine(event.target.value)}
          SelectProps={{ SelectDisplayProps: { 'aria-label': 'engine' } }}
          helperText="blank keeps the engine this tenant plans on"
          sx={{ minWidth: { xs: 0, sm: 200 } }}
        >
          <MenuItem value="">(unchanged)</MenuItem>
          {ENGINES.map(name => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyPlatform}
              onChange={event => setNotifyPlatform(event.target.checked)}
            />
          }
          label="Tell the platform (replans the day for real)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={liveWorld || notifyPlatform}
              disabled={notifyPlatform}
              onChange={event => setLiveWorld(event.target.checked)}
            />
          }
          label="Plan the rides in the world now, not the ones this run saw"
        />
        <Button
          variant="contained"
          onClick={onStart}
          disabled={rerun.isPending}
          sx={{ ml: 'auto' }}
        >
          {changed.length || engine ? `Start re-run (${changed.length + (engine ? 1 : 0)} changed)` : 'Start re-run'}
        </Button>
        <Button onClick={onClose} disabled={rerun.isPending}>
          Cancel
        </Button>
      </Stack>

      {notifyPlatform && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          The platform will be told about this run: it clears the planned day, stages each route
          into it and applies it — replacing whatever was planned for that day before, and planning
          the rides in the world now rather than the ones this run saw. Leave this off to try a
          configuration without touching the night.
        </Alert>
      )}
    </Paper>
  );
};

export default RerunPanel;
